import { Fragment } from "react";
import { createPortal } from "react-dom";
import { Funnel, FunnelX, ListTree, ZoomIn } from "lucide-react";
import { canFilterBy, groupLabel, type PickedValue } from "@cd/viz";
import type { DateZoom } from "../../lib/dateZoom";
import { stepLabel, type DrillStep } from "../../lib/drill";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "../../components/ui/overlay";

/**
 * The menu a click on a widget value opens: filter the whole page to that value, or leave it out, or
 * for a date bucket, zoom the page's dates into it. Its wording names the dashboard, since the click changes every widget that can take it. A menu
 * rather than an instant filter, so a stray tap or a click meant for a tooltip changes nothing.
 * "Break down … by…" comes last and takes the whole click (a bar for Cocktails · Ana breaks down both).
 */
export function PickMenu({ at, picks, onClose, canFilter, onFilter, zoomFor, onZoom, breakDown, onBreakDown }: {
  /** Where the click landed, in viewport coordinates; null when closed. */
  at: { x: number; y: number } | null;
  picks: PickedValue[];
  onClose: () => void;
  /** Whether values can filter the page; without it, only zooms and breakdowns are offered. */
  canFilter: boolean;
  onFilter: (pick: PickedValue, exclude: boolean) => void;
  /** The zoom a date pick makes; undefined for every other pick. */
  zoomFor: (pick: PickedValue) => DateZoom | undefined;
  onZoom: (zoom: DateZoom) => void;
  /** The breakdown the click starts; undefined when it can't start one. */
  breakDown?: DrillStep;
  onBreakDown: (step: DrillStep) => void;
}) {
  const sections = picks.flatMap((p) => {
    const zoom = zoomFor(p);
    return zoom || (canFilter && canFilterBy(p.column)) ? [{ p, zoom }] : [];
  });
  return (
    <Menu open={!!at} onOpenChange={(o) => !o && onClose()} modal={false}>
      {/* The anchor goes on the body: grid items are transformed, which would offset a fixed position inside them. */}
      {at && createPortal(
        <MenuTrigger asChild>
          <span aria-hidden className="pointer-events-none fixed size-px" style={{ left: at.x, top: at.y }} />
        </MenuTrigger>,
        document.body,
      )}
      <MenuContent align="start">
        {sections.map(({ p, zoom }, i) => {
          const value = groupLabel(p.column, p.value);
          return (
            <Fragment key={p.column.key}>
              {i > 0 && <MenuSeparator />}
              <MenuLabel>{p.column.label}</MenuLabel>
              {zoom ? (
                <MenuItem onSelect={() => onZoom(zoom)}>
                  <ZoomIn className="size-4" /> <span className="max-w-64 truncate">Zoom dashboard to {zoom.label}</span>
                </MenuItem>
              ) : (
                <>
                  <MenuItem onSelect={() => onFilter(p, false)}>
                    <Funnel className="size-4" /> <span className="max-w-64 truncate">Filter dashboard to {value}</span>
                  </MenuItem>
                  <MenuItem onSelect={() => onFilter(p, true)}>
                    <FunnelX className="size-4" /> <span className="max-w-64 truncate">Exclude {value} from dashboard</span>
                  </MenuItem>
                </>
              )}
            </Fragment>
          );
        })}
        {breakDown && (
          <>
            {sections.length > 0 && <MenuSeparator />}
            <MenuItem onSelect={() => onBreakDown(breakDown)}>
              <ListTree className="size-4" /> <span className="max-w-64 truncate">Break down {stepLabel(breakDown)} by…</span>
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}
